/**
 * A7Y-4957 — which admin pages each admin role can open.
 *
 * Navigation only: the API enforces the role on every endpoint
 * (`a7y-api/src/middleware/adminRoleScope.ts`), so a page listed here by mistake
 * renders the API's refusals, never data. Patterns are react-router paths matched whole.
 *
 * `marketing` covers Content, the CMO's HELM and CRM pages, support messaging, product
 * emails, promo codes, short links, stats, affiliate program management, and read-only
 * customer lookup (organizations, users, subscriptions).
 */
import type { AdminRole } from '@a7y/types';
import { matchPath } from 'react-router-dom';

export const MARKETING_ADMIN_PAGES: readonly string[] = [
  // Content
  '/admin/content',
  '/admin/content/publishing',
  '/admin/content/feedback',
  '/admin/content/docs',
  '/admin/content/docs/new',
  '/admin/content/docs/:pageId',
  '/admin/content/blog',
  '/admin/content/blog/new',
  '/admin/content/blog/:postId',
  '/admin/blog',
  '/admin/blog/new',
  '/admin/blog/:postId',
  '/admin/announcements',
  '/admin/release-notes',
  '/admin/learning-topics',
  // Marketing / CMO
  '/admin/cmo/helm',
  '/admin/cmo/crm',
  '/admin/cmo/crm/this-week',
  '/admin/cmo/crm/board',
  '/admin/cmo/crm/:accountId',
  '/admin/support',
  '/admin/messaging',
  '/admin/messaging/:threadId',
  '/admin/product-emails',
  '/admin/product-emails/sends/:sendId',
  '/admin/promo-codes',
  '/admin/short-links',
  '/admin/stats/overview',
  '/admin/stats/keyword-database',
  '/admin/affiliates',
  '/admin/affiliates/overview',
  '/admin/affiliates/campaigns',
  '/admin/affiliates/:affiliateId',
  // Customer support (read)
  '/admin/organizations',
  '/admin/organizations/:orgId',
  '/admin/organizations/:orgId/projects/:projectId',
  '/admin/users',
  '/admin/users/:userId',
  '/admin/subscriptions',
  '/admin/subscriptions/:subscriptionId',
];

/**
 * Affiliate pages whose literal segment `/admin/affiliates/:affiliateId` would
 * otherwise match. They are the program's money and fraud queues, which stay
 * with finance.
 */
export const MARKETING_DENIED_AFFILIATE_PAGES: readonly string[] = [
  '/admin/affiliates/commissions',
  '/admin/affiliates/payouts',
  '/admin/affiliates/suspicious',
  '/admin/affiliates/first-hold',
  '/admin/affiliates/reconciliation',
];

const matchesAny = (patterns: readonly string[], pathname: string) =>
  patterns.some((path) => matchPath({ path, end: true }, pathname) !== null);

/** Whether `role` can open the admin page at `pathname`. No role opens nothing. */
export function canAccessAdminPage(role: AdminRole | null | undefined, pathname: string): boolean {
  if (role === 'super_admin') return true;
  if (role !== 'marketing') return false;
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (matchesAny(MARKETING_DENIED_AFFILIATE_PAGES, path)) return false;
  return matchesAny(MARKETING_ADMIN_PAGES, path);
}

/** Where `/admin` takes each role: the dashboard for superadmins, the content queue for marketing. */
export function defaultAdminPage(role: AdminRole | null | undefined): string {
  return role === 'marketing' ? '/admin/content/publishing' : '/admin';
}
